import { useState } from "react";
import axios from "axios";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Field, formatDateTime } from "@/components/admin/ui";
import {
  WORKFLOW_STEPS_ORDER, WORKFLOW_STEP_LABELS, WORKFLOW_STATE_META,
  channelForContactMethod,
} from "./salesMeta";
import { LeadServiceAreaTab } from "./LeadServiceAreaTab";
import { LeadBusinessTab } from "./LeadBusinessTab";
import { AssessmentTab } from "./AssessmentTab";
import { QuoteProposalTab } from "./QuoteProposalTab";
import { LeadFilesTab } from "./LeadFilesTab";

const TABS = ["Overview", "Business", "Service Area", "Assessment", "Quote & Proposal", "Notes", "Files"];

const SaveButton = ({ onClick, busy, children = "Save" }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    data-testid="sales-tab-save-btn"
    className="rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC] disabled:cursor-not-allowed disabled:opacity-50"
  >
    {children}
  </button>
);

const SalesProgress = ({ workflow }) => (
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="sales-progress">
    {WORKFLOW_STEPS_ORDER.map((step, i) => {
      const state = workflow?.[step]?.state || "not_started";
      const meta = WORKFLOW_STATE_META[state];
      return (
        <div key={step} className="rounded-xl border border-slate-200 p-3 dark:border-white/10" data-testid={`sales-progress-step-${step}`}>
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{i + 1} — {WORKFLOW_STEP_LABELS[step]}</p>
          <p className={`mt-1 text-sm font-bold ${meta.cls}`}>{meta.label}</p>
        </div>
      );
    })}
  </div>
);

export const LeadDetailTabs = ({ lead, customer, intakeAttachments, channels, meta, eligibleTeams, eligibleLeadOwners, businessItems, onRefresh, onError, API }) => {
  const [tab, setTab] = useState("Overview");
  const [busy, setBusy] = useState(false);
  const [noteText, setNoteText] = useState("");

  const channelKey = channelForContactMethod(lead.preferred_contact_method);
  const channelConnected = channelKey ? channels?.[channelKey] : null;

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); await onRefresh(); } catch (err) { onError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const addNote = () => { if (!noteText.trim()) return; run(async () => { await axios.post(`${API}/org/sales/leads/${lead.lead_id}/notes`, { text: noteText }, { withCredentials: true }); setNoteText(""); }); };

  return (
    <div data-testid="lead-detail-tabs">
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 dark:border-white/10">
        {TABS.map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} data-testid={`lead-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
            className={`border-b-2 px-3 py-2.5 text-sm font-bold transition-colors duration-150 ${tab === t ? "border-[#0052FF] text-[#0052FF]" : "border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="mt-5 space-y-5" data-testid="lead-tab-panel-overview">
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Sales Progress</p>
            <SalesProgress workflow={lead.workflow} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Requested Service" value={lead.requested_service_name} testId="lead-overview-service" />
            <Field label="Source" value="Nova Form" testId="lead-overview-source" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Original Intake</p>
            <a href={`/dashboard/solutions/front-desk?intake=${lead.intake_submission_id}`} data-testid="lead-overview-view-intake" className="mt-1 inline-block text-sm font-bold text-[#0052FF] hover:underline">
              View Intake {lead.intake_display_id} →
            </a>
          </div>
          {lead.description && <Field label="Customer's Request" value={lead.description} testId="lead-overview-description" />}
          {lead.service_address && (
            <Field label="Service Address" value={`${lead.service_address.street}, ${lead.service_address.city}, ${lead.service_address.state} ${lead.service_address.zip_code}`} testId="lead-overview-address" />
          )}
          <div className="rounded-xl border border-slate-200 p-4 dark:border-white/10" data-testid="lead-overview-customer-card">
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Customer Information</p>
            <p className="mt-1 text-sm font-bold text-slate-900 dark:text-white">{customer?.first_name} {customer?.last_name}</p>
            <p className="text-sm text-slate-600 dark:text-slate-300">{customer?.phone || "—"} · {customer?.email || "—"}</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Preferred: {lead.preferred_contact_method || "—"}
              {channelKey && !channelConnected && <span className="ml-1 font-bold text-amber-600 dark:text-amber-400">— {channelKey === "sms" ? "SMS" : "Email"} not connected</span>}
            </p>
          </div>
        </div>
      )}

      {tab === "Business" && (
        <div className="mt-5">
          <LeadBusinessTab lead={lead} meta={meta} businessItems={businessItems} onRefresh={onRefresh} onError={onError} API={API} />
        </div>
      )}

      {tab === "Service Area" && (
        <div className="mt-5">
          <LeadServiceAreaTab lead={lead} meta={meta} eligibleTeams={eligibleTeams} eligibleLeadOwners={eligibleLeadOwners} onRefresh={onRefresh} onError={onError} API={API} />
        </div>
      )}

      {tab === "Assessment" && (
        <div className="mt-5" data-testid="lead-tab-panel-assessment">
          <AssessmentTab lead={lead} onRefresh={onRefresh} onError={onError} API={API} />
        </div>
      )}

      {tab === "Quote & Proposal" && (
        <div className="mt-5" data-testid="lead-tab-panel-quote-proposal">
          <QuoteProposalTab lead={lead} businessItems={businessItems} onRefresh={onRefresh} onError={onError} API={API} />
        </div>
      )}

      {tab === "Notes" && (
        <div className="mt-5 space-y-4" data-testid="lead-tab-panel-notes">
          <div className="flex gap-2">
            <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2} placeholder="Add an internal note…" data-testid="lead-note-input" className="flex-1 rounded-lg border border-slate-200 p-2.5 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
            <SaveButton onClick={addNote} busy={busy}>Add Note</SaveButton>
          </div>
          {(lead.notes || []).length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">No notes yet.</p>
          ) : (
            <div className="space-y-2" data-testid="lead-notes-list">
              {[...lead.notes].reverse().map((n) => (
                <div key={n.note_id} className="rounded-xl border border-slate-200 p-3 dark:border-white/10">
                  <p className="text-sm text-slate-800 dark:text-slate-200">{n.text}</p>
                  <p className="mt-1 text-xs text-slate-400">{n.author_name} · {formatDateTime(n.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "Files" && (
        <div className="mt-5" data-testid="lead-tab-panel-files">
          <LeadFilesTab lead={lead} intakeAttachments={intakeAttachments} onError={onError} API={API} />
        </div>
      )}
    </div>
  );
};
